import { reactive } from 'vue'
import {
  type PushState,
  subscribeToPush,
  syncSubscription,
  unsubscribeFromPush,
} from '@/push-subscription'

/**
 * @brief Whether this browser is set up to receive push, shared by everything
 *        that shows or changes it.
 *
 * Two places ask: the bell in the navbar, and the notification settings page,
 * where the per-queue toggles are meaningless until this says `subscribed`.
 * They have to agree — a settings page claiming push is off while the bell
 * offers to turn it off is worse than either one alone — so the state lives
 * here rather than once per component.
 */
export const pushDevice = reactive<{
  state: PushState
  busy: boolean
  /**
   * False until the first reconcile has answered. Everything that renders the
   * state waits for it: before the server has been asked, `state` is only the
   * initial guess, and a banner that announced "this browser cannot receive
   * push" for one frame before correcting itself would be worse than nothing.
   */
  reconciled: boolean
}>({
  state: 'unsupported',
  busy: false,
  reconciled: false,
})

/** In flight, so several components mounting at once ask the server once. */
let pending: Promise<PushState> | null = null

/**
 * Reconciles the browser with the server. Cheap and side-effect free unless
 * the two disagree, in which case it drops a subscription this user does not
 * own — see syncSubscription().
 *
 * Never rejects. It is called from click handlers and from onMounted, where a
 * rejection would surface as an unhandled promise and reach Sentry; a reconcile
 * that could not be completed simply leaves the state as it was.
 *
 * `reconciled` is set only when the server actually answered. Marking a failed
 * attempt as answered would leave the browser permanently claiming whatever it
 * had guessed — a colleague's live subscription shown as this user's own, for
 * the rest of the page's life.
 */
export const refreshPushDevice = async (): Promise<PushState> => {
  if (pending === null) {
    pending = syncSubscription()
      .then((state) => {
        pushDevice.reconciled = true

        return state
      })
      .catch(() => pushDevice.state)
      .finally(() => {
        pending = null
      })
  }

  pushDevice.state = await pending

  return pushDevice.state
}

/**
 * @brief The reconcile every page does once, wherever it is mounted.
 *
 * Called from app.ts rather than from the bell, because the bell is not on
 * every page that matters. The user portal has no bell at all, and a browser
 * where an agent signed out and a portal user signed in would otherwise keep
 * the agent's endpoint alive for as long as nobody opened the agent app on it.
 *
 * The components call this too, so a page that renders them before the app-wide
 * call lands still gets an answer; it is a no-op once one has arrived.
 */
export const ensurePushDeviceReconciled = async (): Promise<PushState> => {
  if (pushDevice.reconciled && pending === null) {
    return pushDevice.state
  }

  return refreshPushDevice()
}

/**
 * Has to be called from a click. Both Safari and Chrome refuse a permission
 * prompt without user activation, and a refusal is permanent.
 *
 * Returns false rather than rejecting for every outcome that is not a live
 * subscription, the user saying no included. Nothing here is worth an error on
 * screen, and a rejecting click handler is an unhandled promise.
 */
export const enablePushOnThisDevice = async (): Promise<boolean> => {
  pushDevice.busy = true

  try {
    return await subscribeToPush()
  } catch {
    return false
  } finally {
    pushDevice.busy = false
    await refreshPushDevice()
  }
}

export const disablePushOnThisDevice = async (): Promise<boolean> => {
  pushDevice.busy = true

  try {
    return await unsubscribeFromPush()
  } catch {
    return false
  } finally {
    pushDevice.busy = false
    await refreshPushDevice()
  }
}
